"use client";

import React from "react";
import { Loader, CircleCheck } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
const INTEREST_OPTIONS = ["Boy Names", "Girl Names", "Unique Names", "Trending Names", "Biblical Names"];
function CommunityForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-8 text-center space-y-4 animate-in fade-in duration-500">
        <CircleCheck className="size-16 text-secondary" />
        <h3 className="text-2xl font-bold text-primary-foreground">
          {successMessage}
        </h3>
        <p className="text-primary-foreground/80 max-w-md mx-auto">
          We've added you to our list. Keep an eye on your inbox for the latest name trends!
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-4 bg-transparent border-primary-foreground text-primary-foreground hover:bg-primary-foreground hover:text-primary">
          Sign up another email
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6 text-left max-w-xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="first_name" rules={{
        required: "First name is required",
        minLength: {
          value: 2,
          message: "Must be at least 2 characters"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">First Name</FormLabel>
              <FormControl>
                <Input placeholder="Jane" className="bg-background text-foreground border-transparent focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage className="text-secondary-foreground bg-destructive/80 px-2 py-1 rounded-sm inline-block mt-1" />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-primary-foreground">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="jane@example.com" className="bg-background text-foreground border-transparent focus-visible:ring-secondary" {...field} />
              </FormControl>
              <FormMessage className="text-secondary-foreground bg-destructive/80 px-2 py-1 rounded-sm inline-block mt-1" />
            </FormItem>} />
      </div>

      <FormField control={control} name="interests" render={() => <FormItem className="space-y-3 pt-2">
            <div>
              <FormLabel className="text-primary-foreground text-base">What interests you?</FormLabel>
              <FormDescription className="text-primary-foreground/70">
                Select any categories you'd like to hear about.
              </FormDescription>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {INTEREST_OPTIONS.map((option, i) => <div key={option} data-index={i}>
                  <FormField control={control} name="interests" render={({
            field
          }) => {
            return <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                          <FormControl>
                            <input type="checkbox" checked={field.value?.includes(option)} onChange={e => {
                  const current = field.value || [];
                  const updated = e.target.checked ? [...current, option] : current.filter((val: string) => val !== option);
                  field.onChange(updated);
                }} className="mt-1 size-4 accent-secondary cursor-pointer" />
                          </FormControl>
                          <FormLabel className="font-normal text-primary-foreground cursor-pointer">
                            {option}
                          </FormLabel>
                        </FormItem>;
          }} />
                </div>)}
            </div>
            <FormMessage className="text-secondary-foreground bg-destructive/80 px-2 py-1 rounded-sm inline-block mt-1" />
          </FormItem>} />

      <FormField control={control} name="agree" rules={{
      required: "You must agree to receive updates"
    }} render={({
      field
    }) => <FormItem className="flex flex-row items-start space-x-3 space-y-0 pt-4 border-t border-primary-foreground/20">
            <FormControl>
              <input type="checkbox" checked={field.value} onChange={field.onChange} className="mt-1 size-4 accent-secondary cursor-pointer" />
            </FormControl>
            <div className="space-y-1 leading-none">
              <FormLabel className="text-primary-foreground cursor-pointer">
                I agree to receive updates
              </FormLabel>
              <FormDescription className="text-primary-foreground/70">
                We respect your privacy and will not spam.
              </FormDescription>
            </div>
            <FormMessage className="text-secondary-foreground bg-destructive/80 px-2 py-1 rounded-sm inline-block mt-1" />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/20 border border-destructive/50 rounded-md text-center">
          <p className="text-primary-foreground font-medium">{submitError}</p>
        </div>}

      <div className="pt-4 text-center">
        <Button type="submit" variant="secondary" size="lg" disabled={isSubmitting} className="w-full sm:w-auto min-w-[200px] font-bold text-base">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Subscribing...
            </> : "Subscribe"}
        </Button>
      </div>
    </Form>;
}
export default function JoinOurCommunity() {
  return <section id="join-our-community" className="dark bg-background text-foreground py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-primary text-primary-foreground shadow-xl">
        <div className="p-8 md:p-12 lg:p-16">
          <div className="text-center mb-10">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight mb-4">
              Join Our Community
            </h2>
            <p className="text-lg md:text-xl text-primary-foreground/90 max-w-2xl mx-auto">
              Discover the perfect name. Sign up for our newsletter to receive curated lists, trending names, and exclusive updates directly to your inbox.
            </p>
          </div>

          <FormProvider formId="community_signup_home" sectionName="Join Our Community" defaultValues={{
          first_name: "",
          email: "",
          interests: [],
          agree: false
        }} submitText="Subscribe" successMessage="Welcome to the community!" errorMessage="Oops! Something went wrong. Please try again.">
            <CommunityForm />
          </FormProvider>
        </div>
      </div>
    </section>;
}